/**
 * Reconnect-token persistence (DESIGN 5.7). The token from `welcome` is
 * kept in localStorage with a timestamp so a page refresh inside the
 * server's RECONNECT_SECONDS window rebinds to the same player; anything
 * older is stale and would only earn a `no_room`, so it is not offered.
 *
 * Storage is reached through globalThis and every access is guarded, so
 * this runs unchanged (as a no-op) in node tests.
 */

import { RECONNECT_SECONDS } from '@shared/index.js';

/** The localStorage subset used (structural, injectable). */
export interface StorageLike {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

export const TOKEN_KEY = 'spellweave.session';

/** window.localStorage when running in a browser, null otherwise (or when access throws). */
export function defaultStorage(): StorageLike | null {
  const g = globalThis as { window?: unknown; localStorage?: StorageLike };
  if (typeof g.window === 'undefined') return null;
  try {
    return g.localStorage ?? null;
  } catch {
    return null;
  }
}

export function storeToken(storage: StorageLike | null, token: string, nowMs: number = Date.now()): void {
  if (!storage) return;
  try {
    storage.setItem(TOKEN_KEY, JSON.stringify({ token, at: nowMs }));
  } catch { /* storage full or blocked: reconnect-by-token still works within this page */ }
}

/** The stored token when it was saved within RECONNECT_SECONDS, else null. */
export function loadToken(storage: StorageLike | null, nowMs: number = Date.now()): string | null {
  if (!storage) return null;
  try {
    const raw = storage.getItem(TOKEN_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as { token?: unknown; at?: unknown };
    if (typeof parsed.token !== 'string' || typeof parsed.at !== 'number') return null;
    return nowMs - parsed.at <= RECONNECT_SECONDS * 1000 ? parsed.token : null;
  } catch {
    return null;
  }
}

export function clearToken(storage: StorageLike | null): void {
  if (!storage) return;
  try {
    storage.removeItem(TOKEN_KEY);
  } catch { /* nothing to clear */ }
}
